import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { bp } from 'styles/breakpoints'
import Button from 'components/Button'
import Link from 'components/Link'
import SectionHeader from 'components/SectionHeader'
import Slider from 'react-slick'
import { Spacing50 } from 'styles/sharedStyle'
import trackGTM from 'utils/trackGTM'
import GA4 from 'utils/GA4'
import { getComponentId, useFieldId } from 'utils/optimizely'
import {
  CarouselOutline,
  CarouselWrapper,
  CarouselContainer,
  CtaRow,
  CarouselImageBackgroundContainer,
  CarouselImageBackground,
} from './Carousel.style'
import { fixAudiences } from '../../utils/audiences'
import CarouselControls from './CarouselControls'
import CarouselIndicator from './CarouselIndicator'
import CarouselSlide from './CarouselSlide'
import CarouselCard from './CarouselCard'
import CarouselItemModal from '../CarouselItemModal'

import './Carousel.css'

class Carousel extends Component {
  constructor(props) {
    super(props)

    this.state = {
      currentSlide: 0,
      isDragging: false,
      rtl: false,
      carousel: props.carousel,
      reversedCarousel: [...props.carousel].reverse(),
      windowWidth: null,
      elementId: props.componentName
        ? props.componentName.replace(/ContentfulCompCarousel/gi, '')
        : 'Carousel',
    }
    this.productModalRef = React.createRef()
  }

  componentDidMount() {
    this.updateWindowDimensions()
    window.addEventListener('resize', this.updateWindowDimensions.bind(this))

    const { elementId } = this.state

    this.intervalRef = setInterval(() => {
      const { slider } = this
      const { carousel } = this.props
      const rtl = document.querySelectorAll('html[dir="rtl"]').length > 0
      const { rtl: stateRtl } = this.state

      if (stateRtl !== rtl && slider) {
        this.setState(
          {
            rtl,
          },
          () => {
            if (rtl) {
              slider.slickGoTo(carousel.length)
            }
          }
        )

        clearInterval(this.intervalRef)
      }
    }, 1000)

    if (this.refWrapperCarousel) {
      this.refWrapperCarousel.addEventListener('touchstart', this.touchStart)
      this.refWrapperCarousel.addEventListener('touchmove', this.preventTouch, {
        passive: false,
      })
    }

    this.timeoutRef = setTimeout(() => {
      clearInterval(this.intervalRef)
    }, 5000)

    this.setState({ elementId: getComponentId(elementId) })
  }

  componentWillUnmount() {
    clearInterval(this.intervalRef)
    clearTimeout(this.timeoutRef)

    window.removeEventListener('resize', this.updateWindowDimensions.bind(this))

    if (this.refWrapperCarousel) {
      this.refWrapperCarousel.removeEventListener('touchstart', this.touchStart)
      this.refWrapperCarousel.removeEventListener(
        'touchmove',
        this.preventTouch,
        { passive: false }
      )
    }
  }

  trackGA4Events = (
    carouselType,
    introductionTitle,
    villageName,
    hasMultipleItemsPerView
  ) => {
    switch (carouselType) {
      case 'article':
        GA4(
          hasMultipleItemsPerView
            ? 'carousel_articles_scroll'
            : 'carousel_full_bleed_scroll',
          {
            component_headline: introductionTitle,
            village_name: villageName,
          }
        )
        break
      case 'image':
        GA4('carousel_images_scroll', {
          component_headline: introductionTitle,
          village_name: villageName,
        })
        break
      case 'info':
        GA4('carousel_info_cards_scroll', {
          component_headline: introductionTitle,
          village_name: villageName,
        })
        break
      case 'product':
        GA4('carousel_products_card_scroll', {
          component_headline: introductionTitle,
          village_name: villageName,
        })
        break
      case 'offer':
        GA4('offer_carousel_scroll', {
          component_headline: introductionTitle,
          village_name: villageName,
        })
        break
      case 'service':
        GA4('carousel_services_card_scroll', {
          component_headline: introductionTitle,
          village_name: villageName,
        })
        break
      default:
        break
    }
  }

  updateWindowDimensions = () => {
    this.setState({
      windowWidth: window.innerWidth,
    })
    this.forceUpdate()
  }

  onClick = (e, altText) => {
    const { isDragging } = this.state

    if (isDragging) {
      e.preventDefault()
    }

    trackGTM('carousel', 'carousel', 'brand', altText)
  }

  touchStart = (e) => {
    this.firstClientX = e.touches[0].clientX
    this.firstClientY = e.touches[0].clientY
  }

  // eslint-disable-next-line consistent-return
  preventTouch = (e) => {
    const minValue = 5 // threshold

    this.clientX = e.touches[0].clientX - this.firstClientX
    this.clientY = e.touches[0].clientY - this.firstClientY

    // Vertical scrolling does not work when you start swiping horizontally.
    if (Math.abs(this.clientX) > minValue) {
      e.preventDefault()
      e.returnValue = false
      return false
    }
  }

  render() {
    const {
      audiences,
      background,
      cta,
      image: imageBackground,
      introductionTitle,
      introductionEyebrow,
      introductionContent,
      isBoxed,
      isSmall,
      type,
      productModalContactTitle,
      componentCallerUID,
      enableImageFadeIn,
      variableWidth = true,
      villageName = '',
    } = this.props
    const { carousel, reversedCarousel } = this.state

    let visibleSlides = 1
    const hasMultipleItemsPerView = type === 'multipleView'

    if (hasMultipleItemsPerView) {
      visibleSlides = 3
    }

    const carouselHasMultipleItems = hasMultipleItemsPerView
      ? carousel.length > 3
      : carousel.length > 1

    const { currentSlide, rtl, elementId, windowWidth } = this.state
    const initialSlide = rtl ? carousel.length - 1 : 0
    const initialSideWithOffset = rtl ? carousel.length - 1 - 1 : 0

    const settings = {
      arrows: false,
      draggable: true,
      dots: false,
      infinite: false,
      speed: 500,
      slidesToShow: visibleSlides,
      slidesToScroll: visibleSlides,
      focusOnSelect: false,
      variableWidth,
      initialSlide: hasMultipleItemsPerView
        ? initialSideWithOffset
        : initialSlide,
      rtl: false,
      onInit: () =>
        this.setState({
          currentSlide: carousel.length > 4 && type === 'multipleView' ? 1 : 0,
        }),
      beforeChange: (current, next) =>
        this.setState({ currentSlide: next, isDragging: false }),
      afterChange: (current) =>
        this.setState({ currentSlide: current, isDragging: false }),
      responsive: hasMultipleItemsPerView
        ? [
            {
              breakpoint: parseInt(bp[3].replace('px', ''), 10),
              settings: {
                initialSlide,
                onInit: () => this.setState({ currentSlide: 0 }),
              },
            },
            {
              breakpoint: parseInt(bp[2].replace('px', ''), 10),
              settings: {
                slidesToShow: 2,
                slidesToScroll: 2,
                initialSlide,
              },
            },
            {
              breakpoint: parseInt(bp[1].replace('px', ''), 10),
              settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                initialSlide,
              },
            },
          ]
        : null,
    }

    const hasImage =
      imageBackground &&
      imageBackground.portrait &&
      imageBackground.portrait.gatsbyImageData &&
      imageBackground.landscape &&
      imageBackground.landscape.gatsbyImageData

    const hasAudiences = !!audiences

    const isCarouselWidthOverflowing =
      windowWidth !== null && 341 * carousel.length > windowWidth
    let carouselXOffset = 0
    let showCarouselControls = true
    const centreAlgned =
      componentCallerUID === 'BrandDirectoryPageT05_articlesCarousel' ||
      componentCallerUID === 'ContentfulCompCarouselProductsCar06' ||
      componentCallerUID === 'ContentfulCompCarouselArticleCar01'

    if (centreAlgned && carousel.length >= 4 && !isCarouselWidthOverflowing) {
      carouselXOffset = -170 * (carousel.length - 3)
      settings.initialSlide = 0
      showCarouselControls = false
    }

    if (centreAlgned && isCarouselWidthOverflowing && windowWidth > 1200) {
      carouselXOffset = -170
    }

    let carouselType = ''

    return (
      <CarouselContainer
        id={elementId}
        isCarouselWidthOverflowing={isCarouselWidthOverflowing}
        multipleView={hasMultipleItemsPerView}
        background={background}
        hasAudience={hasAudiences}
        className={fixAudiences(audiences)}>
        {hasImage && (
          <CarouselImageBackgroundContainer>
            <CarouselImageBackground
              image={imageBackground.portrait.gatsbyImageData}
              display={['block', null, 'none']}
            />
            <CarouselImageBackground
              image={imageBackground.landscape.gatsbyImageData}
              display={['none', null, 'block']}
            />
          </CarouselImageBackgroundContainer>
        )}
        {introductionTitle || introductionEyebrow || introductionContent ? (
          <SectionHeader
            {...{
              title: introductionTitle,
              eyebrow: introductionEyebrow,
              content: introductionContent,
              background,
              withMaxWidth: true,
              hasImage,
              parentElementId: elementId,
            }}
          />
        ) : (
          <Spacing50 />
        )}

        {carousel && carousel.length && (
          <CarouselOutline carouselXOffset={carouselXOffset} rtl={rtl}>
            <CarouselWrapper
              dir="ltr"
              ref={(wrapper) => {
                this.refWrapperCarousel = wrapper
              }}
              multipleView={hasMultipleItemsPerView}
              background={background}
              withPagination={carouselHasMultipleItems}>
              <Slider
                onSwipe={() => {
                  this.trackGA4Events(
                    carouselType,
                    introductionTitle,
                    villageName,
                    hasMultipleItemsPerView
                  )
                  GA4('carousel_click', { village_name: villageName })
                }}
                ref={(slider) => {
                  this.slider = slider
                }}
                {...settings}>
                {(rtl ? reversedCarousel : carousel).map(
                  (
                    {
                      image,
                      article,
                      product,
                      service,
                      offer,
                      info,
                      brand,
                      video,
                      moreImages,
                    },
                    index
                  ) => {
                    const brandLink = product?.brandLink
                    const showBrandLink = product?.showBrandLink
                    const vsPhoneContact = product?.virtualShoppingPhone
                    const vsEmailContact = product?.virtualShoppingEmail
                    const vsWhatsappContact = product?.virtualShoppingWhatsapp
                    const vsProductCatalogueContact =
                      product?.virtualShoppingProductCatalogue
                    const vsBookingContact = product?.virtualShoppingBookingUrl

                    const contactMethodsFormatter = (item) => {
                      if (!item) return null

                      const virtualServices = []

                      if (
                        item.whatsappLabel &&
                        item.whatsappIcon &&
                        vsWhatsappContact
                      ) {
                        virtualServices.push({
                          icon: item.whatsappIcon,
                          label: item.whatsappLabel,
                          contact: `https://wa.me/${vsWhatsappContact}`,
                        })
                      }

                      if (
                        item.productCatalogueLabel &&
                        item.productCatalogueIcon &&
                        vsProductCatalogueContact
                      ) {
                        virtualServices.push({
                          icon: item.productCatalogueIcon,
                          label: item.productCatalogueLabel,
                          contact: vsProductCatalogueContact,
                        })
                      }

                      if (
                        item.bookingUrlLabel &&
                        item.bookingUrlIcon &&
                        vsBookingContact
                      ) {
                        virtualServices.push({
                          icon: item.bookingUrlIcon,
                          label: item.bookingUrlLabel,
                          contact: vsBookingContact,
                        })
                      }

                      if (
                        item.emailAddressLabel &&
                        item.emailAddressIcon &&
                        vsEmailContact
                      ) {
                        virtualServices.push({
                          icon: item.emailAddressIcon,
                          label: item.emailAddressLabel,
                          contact: `mailto:${vsEmailContact}`,
                        })
                      }

                      if (
                        item.phoneNumberLabel &&
                        item.phoneNumberIcon &&
                        vsPhoneContact
                      ) {
                        virtualServices.push({
                          icon: item.phoneNumberIcon,
                          label: item.phoneNumberLabel,
                          contact: `tel:${vsPhoneContact}`,
                        })
                      }

                      return virtualServices
                    }

                    const contactMethods = contactMethodsFormatter(
                      product?.villageLevelLabels
                    )

                    if (image) carouselType = 'image'
                    if (article) carouselType = 'article'
                    if (product) carouselType = 'product'
                    if (service) carouselType = 'service'
                    if (offer) carouselType = 'offer'
                    if (info) carouselType = 'info'
                    if (brand) carouselType = 'brand'

                    return (
                      <>
                        <div
                          key={`carousel_item_${index.toString()}`}
                          className={
                            index === carousel.length - 1
                              ? 'scroll-snap-item-end'
                              : 'scroll-snap-item-start'
                          }>
                          {hasMultipleItemsPerView ? (
                            <CarouselCard
                              isLast={carousel.length - 1 === currentSlide}
                              {...{
                                currentSlide,
                                product,
                                article,
                                service,
                                offer,
                                info,
                                brand,
                                image,
                                isSmall,
                                index,
                                hasImage,
                                rtl,
                                elementId: useFieldId(
                                  elementId,
                                  `card-${index + 1}`
                                ),
                                enableImageFadeIn,
                                onclickProductModal: () => {
                                  this.productModalRef.current.openModal({
                                    ...product,
                                    image,
                                    brandLink,
                                    showBrandLink,
                                    contactMethods,
                                    moreImages,
                                    productModalContactTitle,
                                  })
                                  if (carouselType === 'product') {
                                    GA4('carousel_products_card_click', {
                                      headline: product.description,
                                      component_headline: introductionTitle,
                                      village_name: villageName,
                                    })
                                  }
                                },
                                isColoredBg: background === 'Grey',
                                background,
                              }}
                              onClick={(event) => {
                                this.onClick(event, image.altText)
                                if (carouselType === 'article') {
                                  GA4('carousel_articles_click', {
                                    headline: article.heading,
                                    component_headline: introductionTitle,
                                    village_name: villageName,
                                  })
                                }
                                if (carouselType === 'service') {
                                  GA4('carousel_services_card_click', {
                                    headline: service.heading,
                                    component_headline: introductionTitle,
                                    village_name: villageName,
                                  })
                                }
                                if (carouselType === 'product') {
                                  GA4('carousel_products_card_click', {
                                    headline: product.description,
                                    component_headline: introductionTitle,
                                    village_name: villageName,
                                  })
                                }
                              }}
                            />
                          ) : (
                            <CarouselSlide
                              {...{
                                currentSlide,
                                heading:
                                  article && article.heading
                                    ? article.heading
                                    : null,
                                publishDate:
                                  article && article.publishDate
                                    ? article.publishDate
                                    : null,
                                eyebrow:
                                  article && article.eyebrow
                                    ? article.eyebrow
                                    : null,
                                portrait: image.portrait,
                                landscape: image.landscape,
                                altText: image.altText,
                                index,
                                rtl,
                                url:
                                  article && article.url ? article.url : null,
                                video,
                                elementId: useFieldId(
                                  elementId,
                                  `slide-${index + 1}`
                                ),
                              }}
                              onClick={(event) => {
                                this.onClick(event, image.altText)
                                if (carouselType === 'article') {
                                  GA4('carousel_full_bleed_click', {
                                    component_headline: introductionTitle,
                                    headline: article.heading,
                                    village_name: villageName,
                                  })
                                }
                              }}
                            />
                          )}
                        </div>
                      </>
                    )
                  }
                )}
              </Slider>
              {!hasMultipleItemsPerView && carouselHasMultipleItems && (
                <CarouselIndicator
                  currentSlide={currentSlide}
                  totalSlides={carousel.length}
                  rtl={rtl}
                />
              )}
              {carouselHasMultipleItems && showCarouselControls && (
                <CarouselControls
                  onSlideChange={(index) => this.slider.slickGoTo(index)}
                  currentSlide={currentSlide}
                  totalSlides={carousel.length}
                  hasMultipleItemsPerView={hasMultipleItemsPerView}
                  isBoxed={isBoxed}
                  isSmall={isSmall}
                  rtl={rtl}
                  villageName={villageName}
                  carouselType={carouselType}
                  introductionTitle={introductionTitle}
                />
              )}
            </CarouselWrapper>
          </CarouselOutline>
        )}
        {!!cta && ['copy', 'url'].every((key) => !!cta[key]) && (
          <>
            <Spacing50 />
            <CtaRow>
              <Button
                as={Link}
                level="primary"
                to={cta.url}
                external={cta.url.indexOf('http') === 0}
                backgroundColor={background}>
                {cta.copy}
              </Button>
            </CtaRow>
          </>
        )}
        <Spacing50 />
        <CarouselItemModal ref={this.productModalRef} />
      </CarouselContainer>
    )
  }
}

Carousel.propTypes = {
  componentName: PropTypes.string,
  introductionTitle: PropTypes.string,
  introductionEyebrow: PropTypes.string,
  productModalContactTitle: PropTypes.string,
  introductionContent: PropTypes.shape({
    childMarkdownRemark: PropTypes.shape({
      html: PropTypes.string,
    }),
  }),
  componentCallerUID: PropTypes.string,
  carousel: PropTypes.arrayOf(
    PropTypes.shape({
      altText: PropTypes.string,
      image: PropTypes.shape({
        portrait: PropTypes.shape({
          gatsbyImageData: PropTypes.shape({
            aspectRatio: PropTypes.number.isRequired,
            src: PropTypes.string.isRequired,
            srcSet: PropTypes.string.isRequired,
            srcSetWebp: PropTypes.string.isRequired,
            sizes: PropTypes.string.isRequired,
          }).isRequired,
        }),
        landscape: PropTypes.shape({
          gatsbyImageData: PropTypes.shape({
            aspectRatio: PropTypes.number.isRequired,
            src: PropTypes.string.isRequired,
            srcSet: PropTypes.string.isRequired,
            srcSetWebp: PropTypes.string.isRequired,
            sizes: PropTypes.string.isRequired,
          }).isRequired,
        }),
      }),
      product: PropTypes.shape({
        brand: PropTypes.string.isRequired,
        // currency: PropTypes.string,
        description: PropTypes.string.isRequired,
        locale: PropTypes.string,
        rrpPrice: PropTypes.number,
        salePrice: PropTypes.number,
        villagePrice: PropTypes.number,
        audiences: PropTypes.string,
      }),
      article: PropTypes.shape({
        eyebrow: PropTypes.string,
        url: PropTypes.string,
        publishDate: PropTypes.string,
        heading: PropTypes.string,
      }),
      service: PropTypes.shape({
        eyebrow: PropTypes.string,
        benefit: PropTypes.string,
        bodyCopy: PropTypes.string,
        heading: PropTypes.string,
        url: PropTypes.string,
        external: PropTypes.bool,
      }),
      offer: PropTypes.shape({
        eyebrow: PropTypes.string.isRequired,
        headline: PropTypes.string.isRequired,
        offerRunStartDate: PropTypes.string.isRequired,
        offerRunFinishDate: PropTypes.string.isRequired,
        offerExpirationLabel: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        audiences: PropTypes.string,
      }),
      info: PropTypes.shape({
        bodyCopy: PropTypes.string,
        heading: PropTypes.string,
      }),
      brand: PropTypes.shape({
        summary: PropTypes.string,
        bodyCopy: PropTypes.string,
        logo: PropTypes.shape({
          code: PropTypes.shape({ code: PropTypes.string }),
        }),
      }),
    })
  ),
  type: PropTypes.string,
  background: PropTypes.string,
  isBoxed: PropTypes.bool,
  isSmall: PropTypes.bool,
  cta: PropTypes.shape({
    copy: PropTypes.string,
    url: PropTypes.string,
  }),
  image: PropTypes.shape({
    altText: PropTypes.string,
    portrait: PropTypes.shape({
      gatsbyImageData: PropTypes.shape({
        aspectRatio: PropTypes.number.isRequired,
        src: PropTypes.string.isRequired,
        srcSet: PropTypes.string.isRequired,
        srcSetWebp: PropTypes.string.isRequired,
        sizes: PropTypes.string.isRequired,
      }).isRequired,
    }),
    landscape: PropTypes.shape({
      gatsbyImageData: PropTypes.shape({
        aspectRatio: PropTypes.number.isRequired,
        src: PropTypes.string.isRequired,
        srcSet: PropTypes.string.isRequired,
        srcSetWebp: PropTypes.string.isRequired,
        sizes: PropTypes.string.isRequired,
      }).isRequired,
    }),
  }),
  audiences: PropTypes.string,
  variableWidth: PropTypes.bool,
  enableImageFadeIn: PropTypes.bool,
  villageName: PropTypes.string,
}

Carousel.defaultProps = {
  componentName: null,
  productModalContactTitle: 'Virtual Shopping',
  audiences: '',
  introductionTitle: '',
  introductionEyebrow: '',
  introductionContent: null,
  carousel: [],
  background: 'White',
  type: 'normal',
  isBoxed: false,
  isSmall: false,
  cta: {},
  image: {},
  componentCallerUID: '',
  variableWidth: true,
  enableImageFadeIn: false,
  villageName: '',
}

export default Carousel
